import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiPatch, apiDownload } from '../../../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Download, FileText, CalendarPlus, Star, UserCheck,
  CheckCircle2, ClipboardCheck, ShieldCheck, AlertTriangle,
} from 'lucide-react';
import { formatDate } from '../../../lib/format';
import { useAuth } from '../../../contexts/AuthContext';

const STAGE_LABELS = {
  applied: 'Applied', screening: 'Screening', assessment: 'Assessment',
  interview_l1: 'Interview L1', interview_l2: 'Interview L2',
  interview_l3: 'Interview L3', offer: 'Offer', accepted: 'Accepted',
  rejected: 'Rejected', withdrawn: 'Withdrawn', joined: 'Joined',
};

const STAGE_COLOR = {
  offer:     'bg-amber-500', accepted: 'bg-emerald-600',
  joined:    'bg-emerald-700', rejected: 'bg-red-500',
  withdrawn: 'bg-slate-400', assessment: 'bg-violet-600',
};

export default function CandidateDetail() {
  const { candidateId } = useParams();
  const cid = candidateId;
  const nav = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [ivForm, setIvForm] = useState({ round: 'L1', scheduled_at: '', interviewer_name: '', mode: 'in-person' });
  const [showIv, setShowIv] = useState(false);
  // Offer accept form
  const [showOfferAccept, setShowOfferAccept] = useState(false);
  const [acceptForm, setAcceptForm] = useState({ accepted_on: new Date().toISOString().slice(0, 10), notes: '' });
  // Assessment form
  const [showAssessment, setShowAssessment] = useState(false);
  const [assForm, setAssForm] = useState({ test_name: '', score: '', max_score: '', conducted_on: '', conducted_by: '', pass_threshold: '' });
  // BGV form
  const [showBgvInit, setShowBgvInit] = useState(false);
  const [showBgvComplete, setShowBgvComplete] = useState(false);
  const [bgvForm, setBgvForm] = useState({ agency: '', initiated_on: '', expected_by: '' });
  const [bgvCompleteForm, setBgvCompleteForm] = useState({ result: 'clear', remarks: '', completed_on: '' });

  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toUpperCase();
  const canWrite = ['super_admin', 'admin', 'hr', 'md'].includes(role) || dept === 'HR' || dept === 'MD';

  const load = useCallback(() => apiGet(`/hr/candidates/${cid}`).then(setC).catch(() => toast.error('Candidate not found')), [cid]);
  useEffect(() => { load(); }, [load]);

  const scheduleIv = async (e) => {
    e.preventDefault();
    if (!ivForm.round) { toast.error('Round is required'); return; }
    try {
      await apiPost(`/hr/candidates/${cid}/interview`, ivForm);
      toast.success('Interview scheduled'); setShowIv(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const saveFeedback = async (round, feedback, rating, outcome) => {
    try {
      await apiPut(`/hr/candidates/${cid}/interview/${round}/feedback`, { feedback, rating, outcome });
      toast.success('Feedback saved'); load();
    } catch (err) { toast.error('Failed to save feedback'); }
  };

  const acceptOffer = async (e) => {
    e.preventDefault();
    try {
      await apiPost(`/hr/candidates/${cid}/offer/accept`, acceptForm);
      toast.success('Offer acceptance recorded'); setShowOfferAccept(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const recordAssessment = async (e) => {
    e.preventDefault();
    if (!assForm.test_name || !assForm.score || !assForm.max_score || !assForm.conducted_on) {
      toast.error('Fill all required fields'); return;
    }
    try {
      await apiPost(`/hr/candidates/${cid}/assessment`, {
        ...assForm,
        score: parseFloat(assForm.score),
        max_score: parseFloat(assForm.max_score),
        pass_threshold: assForm.pass_threshold ? parseFloat(assForm.pass_threshold) : undefined,
      });
      toast.success('Assessment recorded'); setShowAssessment(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const initiateBgv = async (e) => {
    e.preventDefault();
    if (!bgvForm.initiated_on) { toast.error('Initiation date is required'); return; }
    try {
      await apiPost(`/hr/candidates/${cid}/bgv/initiate`, bgvForm);
      toast.success('BGV initiated'); setShowBgvInit(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const completeBgv = async (e) => {
    e.preventDefault();
    try {
      await apiPatch(`/hr/candidates/${cid}/bgv/complete`, bgvCompleteForm);
      toast.success('BGV completed'); setShowBgvComplete(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const join = async () => {
    if (!window.confirm(`Mark ${c.name} as joined? An employee record will be created.`)) return;
    try {
      const r = await apiPost(`/hr/candidates/${cid}/join`, {});
      toast.success(`Employee created (${r.emp_id})`);
      nav(`/app/hr/employees?edit=${r.employee_id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Join failed'); }
  };

  if (!c) return <div className="p-8 text-slate-500">Loading…</div>;

  const stageColor = STAGE_COLOR[c.stage] || 'bg-[#1F7A6E]';

  return (
    <div className="p-6 md:p-8 space-y-5 max-w-4xl" data-testid="candidate-detail-page">
      <button onClick={() => nav(-1)} className="text-xs text-slate-500 flex items-center gap-1 hover:text-[#1F7A6E]" data-testid="cand-back">
        <ArrowLeft size={14} /> Back
      </button>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">{c.name}</h1>
          <p className="text-sm text-slate-600 mt-1">{c.email || '—'} · {c.phone || '—'} · Source: {c.source}</p>
          <span className={`inline-block mt-2 text-[11px] font-display font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm text-white ${stageColor}`}>{STAGE_LABELS[c.stage] || c.stage}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {c.resume_url && (
            <button onClick={() => apiDownload(`/hr/candidates/${cid}/resume`, `resume_${c.name}.pdf`)} data-testid="download-resume-btn"
              className="px-3 py-2 border border-slate-200 rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-slate-50 flex items-center gap-1.5"><Download size={13} /> Resume</button>
          )}
          {c.offer?.offer_letter_url && (
            <button onClick={() => apiDownload(`/hr/candidates/${cid}/offer-letter`, `offer_${c.name}.pdf`)} data-testid="download-offer-btn"
              className="px-3 py-2 border border-amber-300 text-amber-700 rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-amber-50 flex items-center gap-1.5"><FileText size={13} /> Offer Letter</button>
          )}
          {/* Offer Accept button — only when stage == 'offer' */}
          {canWrite && c.stage === 'offer' && (
            <button onClick={() => setShowOfferAccept(!showOfferAccept)} data-testid="accept-offer-btn"
              className="px-3 py-2 bg-amber-500 text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-amber-600 flex items-center gap-1.5"><CheckCircle2 size={13} /> Accept Offer</button>
          )}
          {/* Mark Joined — shows for both 'accepted' and 'offer' stage */}
          {canWrite && ['accepted', 'offer'].includes(c.stage) && (
            <button onClick={join} data-testid="mark-joined-btn"
              className="px-3 py-2 bg-emerald-600 text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-emerald-700 flex items-center gap-1.5"><UserCheck size={13} /> Mark Joined</button>
          )}
        </div>
      </div>

      {/* Offer Accept form */}
      {showOfferAccept && (
        <form onSubmit={acceptOffer} className="bg-amber-50 border border-amber-200 rounded-sm p-4 space-y-3" data-testid="offer-accept-form">
          <div className="text-sm font-display font-bold text-amber-900">Record Offer Acceptance</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Accepted On *</label><input type="date" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={acceptForm.accepted_on} onChange={e => setAcceptForm({ ...acceptForm, accepted_on: e.target.value })} data-testid="accept-date-input" /></div>
            <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Notes</label><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={acceptForm.notes} onChange={e => setAcceptForm({ ...acceptForm, notes: e.target.value })} placeholder="e.g. Verbal acceptance confirmed over phone" /></div>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 bg-amber-600 text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider" data-testid="confirm-accept-btn">Confirm Acceptance</button>
            <button type="button" onClick={() => setShowOfferAccept(false)} className="px-3 py-2 border border-slate-300 text-slate-600 rounded-sm text-xs font-bold">Cancel</button>
          </div>
        </form>
      )}

      {/* Details grid */}
      <div className="grid md:grid-cols-2 gap-4">
        <Info label="Current Company" v={c.current_company} />
        <Info label="Expected CTC" v={c.expected_ctc ? `₹${Number(c.expected_ctc).toLocaleString('en-IN')}` : null} />
        <Info label="Current CTC" v={c.current_ctc ? `₹${Number(c.current_ctc).toLocaleString('en-IN')}` : null} />
        <Info label="Notice Period" v={c.notice_period_days ? `${c.notice_period_days} days` : null} />
        {c.offer && <Info label="Offered CTC" v={`₹${Number(c.offer.ctc).toLocaleString('en-IN')} · Join ${c.offer.joining_date}`} />}
        {c.offer?.accepted && <Info label="Offer Accepted On" v={c.offer.accepted_at} />}
        {c.rejection_reason && <Info label="Rejection Reason" v={c.rejection_reason} />}
      </div>

      {/* Assessment section */}
      <div className="bg-white border border-slate-200 rounded-sm" data-testid="assessment-section">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2"><ClipboardCheck size={14} /> Assessment / Written Test</h3>
          {canWrite && !c.assessment && (
            <button onClick={() => setShowAssessment(!showAssessment)} data-testid="add-assessment-btn" className="text-xs text-[#1F7A6E] font-bold">+ Record</button>
          )}
        </div>
        {c.assessment && (
          <div className="p-4 flex items-center gap-4" data-testid="assessment-result">
            <div>
              <div className="font-display font-bold text-sm">{c.assessment.test_name}</div>
              <div className="text-xs text-slate-500">{c.assessment.score}/{c.assessment.max_score} · {c.assessment.conducted_on} · {c.assessment.conducted_by || 'HR'}</div>
            </div>
            <span className={`text-[11px] font-display font-bold uppercase px-2 py-0.5 rounded-sm border ml-auto ${c.assessment.result === 'pass' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>{c.assessment.result}</span>
          </div>
        )}
        {!c.assessment && !showAssessment && <div className="px-4 py-3 text-xs text-slate-400">No assessment recorded.</div>}
        {showAssessment && (
          <form onSubmit={recordAssessment} className="p-4 border-t bg-slate-50 space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Test Name *</label><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={assForm.test_name} onChange={e => setAssForm({ ...assForm, test_name: e.target.value })} placeholder="Technical Assessment Round 1" data-testid="ass-test-name" /></div>
              <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Conducted On *</label><input type="date" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={assForm.conducted_on} onChange={e => setAssForm({ ...assForm, conducted_on: e.target.value })} data-testid="ass-date" /></div>
              <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Score *</label><input type="number" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={assForm.score} onChange={e => setAssForm({ ...assForm, score: e.target.value })} data-testid="ass-score" /></div>
              <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Max Score *</label><input type="number" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={assForm.max_score} onChange={e => setAssForm({ ...assForm, max_score: e.target.value })} data-testid="ass-max-score" /></div>
              <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Pass Threshold (optional)</label><input type="number" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={assForm.pass_threshold} onChange={e => setAssForm({ ...assForm, pass_threshold: e.target.value })} /></div>
              <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Conducted By</label><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={assForm.conducted_by} onChange={e => setAssForm({ ...assForm, conducted_by: e.target.value })} /></div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="px-4 py-2 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase" data-testid="ass-submit-btn">Save Assessment</button>
              <button type="button" onClick={() => setShowAssessment(false)} className="px-3 py-2 border border-slate-300 text-slate-600 rounded-sm text-xs font-bold">Cancel</button>
            </div>
          </form>
        )}
      </div>

      {/* Interviews */}
      <div className="bg-white border border-slate-200 rounded-sm" data-testid="interviews-section">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-xs font-display font-bold uppercase tracking-wider text-slate-500">Interviews</h3>
          {canWrite && <button onClick={() => setShowIv(!showIv)} data-testid="schedule-interview-btn" className="text-xs text-[#1F7A6E] font-bold flex items-center gap-1"><CalendarPlus size={13} /> Schedule</button>}
        </div>
        {showIv && (
          <form onSubmit={scheduleIv} className="p-4 border-b bg-slate-50 grid sm:grid-cols-4 gap-2 items-end" data-testid="interview-form">
            <label className="block"><span className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Round</span>
              <select className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={ivForm.round} onChange={(e) => setIvForm({ ...ivForm, round: e.target.value })}>
                {['L1', 'L2', 'L3'].map((r) => <option key={r} value={r}>{r}</option>)}
              </select></label>
            <label className="block"><span className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Date/Time</span><input type="datetime-local" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={ivForm.scheduled_at} onChange={(e) => setIvForm({ ...ivForm, scheduled_at: e.target.value })} /></label>
            <label className="block"><span className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Interviewer</span><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={ivForm.interviewer_name} onChange={(e) => setIvForm({ ...ivForm, interviewer_name: e.target.value })} /></label>
            <button type="submit" data-testid="iv-submit-btn" className="py-2 bg-[#1F7A6E] text-white rounded-sm text-[11px] font-display font-bold uppercase tracking-wider">Add</button>
          </form>
        )}
        <div className="divide-y divide-slate-100">
          {(c.interviews || []).length === 0 ? <div className="p-4 text-xs text-slate-400">No interviews scheduled.</div>
            : c.interviews.map((iv, i) => <InterviewRow key={i} iv={iv} canWrite={canWrite} onSave={saveFeedback} />)}
        </div>
      </div>

      {/* BGV section — shown after offer accepted */}
      {['accepted', 'joined'].includes(c.stage) && (
        <div className="bg-white border border-slate-200 rounded-sm" data-testid="bgv-section">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h3 className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2"><ShieldCheck size={14} /> Background Verification (BGV)</h3>
            {canWrite && !c.bgv && <button onClick={() => setShowBgvInit(!showBgvInit)} data-testid="initiate-bgv-btn" className="text-xs text-[#1F7A6E] font-bold">+ Initiate</button>}
            {canWrite && c.bgv?.status === 'pending' && <button onClick={() => setShowBgvComplete(!showBgvComplete)} data-testid="complete-bgv-btn" className="text-xs text-emerald-700 font-bold">Mark Complete</button>}
          </div>
          {c.bgv ? (
            <div className="p-4 flex items-start gap-4" data-testid="bgv-status">
              <div className="flex-1">
                <div className="font-display font-bold text-sm">{c.bgv.agency || 'Internal BGV'}</div>
                <div className="text-xs text-slate-500">Initiated: {c.bgv.initiated_on} · {c.bgv.checks?.join(', ') || 'Standard checks'}</div>
                {c.bgv.remarks && <div className="text-xs text-slate-400 mt-1">{c.bgv.remarks}</div>}
              </div>
              <span className={`text-[11px] font-display font-bold uppercase px-2 py-0.5 rounded-sm border shrink-0 ${c.bgv.result === 'clear' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : c.bgv.result === 'adverse' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                {c.bgv.result || c.bgv.status}
              </span>
            </div>
          ) : !showBgvInit && (
            <div className="px-4 py-3 text-xs text-slate-400">BGV not yet initiated.</div>
          )}
          {showBgvInit && (
            <form onSubmit={initiateBgv} className="p-4 border-t bg-slate-50 space-y-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Agency</label><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={bgvForm.agency} onChange={e => setBgvForm({ ...bgvForm, agency: e.target.value })} placeholder="AuthBridge / internal" /></div>
                <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Initiated On *</label><input type="date" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={bgvForm.initiated_on} onChange={e => setBgvForm({ ...bgvForm, initiated_on: e.target.value })} data-testid="bgv-initiated-date" /></div>
                <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Expected By</label><input type="date" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={bgvForm.expected_by} onChange={e => setBgvForm({ ...bgvForm, expected_by: e.target.value })} /></div>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="px-4 py-2 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase" data-testid="bgv-submit-btn">Initiate BGV</button>
                <button type="button" onClick={() => setShowBgvInit(false)} className="px-3 py-2 border border-slate-300 text-slate-600 rounded-sm text-xs font-bold">Cancel</button>
              </div>
            </form>
          )}
          {showBgvComplete && (
            <form onSubmit={completeBgv} className="p-4 border-t bg-slate-50 space-y-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Result *</label>
                  <select className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={bgvCompleteForm.result} onChange={e => setBgvCompleteForm({ ...bgvCompleteForm, result: e.target.value })} data-testid="bgv-result-select">
                    {['clear', 'adverse', 'pending'].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Completed On</label><input type="date" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={bgvCompleteForm.completed_on} onChange={e => setBgvCompleteForm({ ...bgvCompleteForm, completed_on: e.target.value })} /></div>
                <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Remarks</label><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={bgvCompleteForm.remarks} onChange={e => setBgvCompleteForm({ ...bgvCompleteForm, remarks: e.target.value })} /></div>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-sm text-xs font-display font-bold uppercase" data-testid="bgv-complete-btn">Save BGV Result</button>
                <button type="button" onClick={() => setShowBgvComplete(false)} className="px-3 py-2 border border-slate-300 text-slate-600 rounded-sm text-xs font-bold">Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Stage Timeline */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <h3 className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 mb-3">Stage Timeline</h3>
        <div className="space-y-2.5">
          {(c.stage_history || []).map((h, i) => (
            <div key={i} className="flex items-start gap-3 text-xs" data-testid={`timeline-${i}`}>
              <div className="w-2 h-2 rounded-full bg-[#1F7A6E] mt-1.5 shrink-0" />
              <div>
                <span className="font-display font-bold">{STAGE_LABELS[h.stage] || h.stage}</span>
                <span className="text-slate-400"> · {formatDate(h.moved_at)} · {h.moved_by || 'system'}</span>
                {h.notes && <div className="text-slate-500">{h.notes}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InterviewRow({ iv, canWrite, onSave }) {
  const [edit, setEdit] = useState(false);
  const [fb, setFb] = useState(iv.feedback || '');
  const [rt, setRt] = useState(iv.rating || 0);
  const [oc, setOc] = useState(iv.outcome || 'hold');
  return (
    <div className="p-4" data-testid={`interview-${iv.round}`}>
      <div className="flex items-center justify-between">
        <div>
          <span className="font-display font-bold text-sm">Round {iv.round}</span>
          <span className="text-xs text-slate-500"> · {iv.scheduled_at ? formatDate(iv.scheduled_at) : 'unscheduled'} · {iv.interviewer_name || 'TBD'} · {iv.mode}</span>
        </div>
        {iv.outcome && <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm border ${iv.outcome === 'pass' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : iv.outcome === 'fail' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-slate-100 text-slate-500 border-slate-300'}`}>{iv.outcome}</span>}
      </div>
      {iv.feedback && !edit && <p className="text-xs text-slate-600 mt-1.5">{iv.feedback} {iv.rating ? <span className="text-amber-500">· {iv.rating}★</span> : null}</p>}
      {canWrite && !edit && <button onClick={() => setEdit(true)} className="text-[11px] text-[#1F7A6E] font-bold mt-1.5" data-testid={`fb-edit-${iv.round}`}>{iv.feedback ? 'Edit feedback' : 'Add feedback'}</button>}
      {edit && (
        <div className="mt-2 space-y-2 bg-slate-50 p-3 rounded-sm">
          <textarea rows={2} className="inp w-full" placeholder="Feedback…" value={fb} onChange={(e) => setFb(e.target.value)} data-testid={`fb-text-${iv.round}`} />
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">{[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" onClick={() => setRt(n)} data-testid={`fb-star-${iv.round}-${n}`}><Star size={16} className={n <= rt ? 'fill-amber-400 text-amber-400' : 'text-slate-300'} /></button>)}</div>
            <select className="inp w-auto" value={oc} onChange={(e) => setOc(e.target.value)}>
              {['pass', 'fail', 'hold'].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <button onClick={() => { onSave(iv.round, fb, rt, oc); setEdit(false); }} data-testid={`fb-save-${iv.round}`} className="px-3 py-1.5 bg-[#1F7A6E] text-white rounded-sm text-[11px] font-display font-bold uppercase">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

const Info = ({ label, v }) => v ? (
  <div className="bg-white border border-slate-200 rounded-sm px-3 py-2">
    <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-400">{label}</div>
    <div className="text-sm mt-0.5">{v}</div>
  </div>
) : null;
